<template>
  <div>
    <!-- Hero -->
    <section class="compare-hero">
      <v-container class="py-12 py-md-16">
        <div class="text-center mb-8">
          <v-chip variant="tonal" color="primary" class="mb-4">Comparison</v-chip>
          <h1 class="compare-h1 mb-4">Song7: a cross-platform alternative to OnSong</h1>
          <p class="text-h6 text-medium-emphasis mx-auto" style="max-width: 820px">
            OnSong is an Apple-only chord chart app for individual musicians. It's a deep, hardware-rich tool for
            reading and performing songs on iPad, iPhone, or Mac. Song7 is a cross-platform, team-first worship platform
            that manages your song library, setlists, transposition, and live presentation in one browser-based tool
            that runs on any device, including Windows and Android.
          </p>
          <p class="text-caption text-medium-emphasis mt-4">
            Last updated June 2026 · Honest comparison · Updated quarterly
          </p>

          <div class="mt-6">
            <a href="#migrate" class="switcher-callout">
              <v-icon icon="$check" size="18" class="mr-1" />
              Already on OnSong? See how to switch to Song7
              <v-icon icon="$arrowRight" size="16" class="ml-1" />
            </a>
          </div>
        </div>

        <v-row class="mt-4" justify="center">
          <v-col cols="12" md="5">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <div class="d-flex align-center mb-4">
                <v-icon icon="$musicCircleOutline" color="primary" size="32" class="mr-3" />
                <strong class="text-h6">Song7</strong>
              </div>
              <p class="text-body-2 mb-3">
                All-in-one worship platform with chord charts, setlists, transposition, real-time team sync, and
                presentation. Web-first, works on any device with a browser.
              </p>
              <ul class="compare-quick-facts">
                <li><strong>Platforms:</strong> Any browser; installable as a PWA (incl. Windows &amp; Android)</li>
                <li><strong>Built for:</strong> Whole worship teams</li>
                <li>
                  <strong>Pricing:</strong> Free for teams up to {{ FREE_PLAN.members }} members; paid plans beyond that
                </li>
              </ul>
              <v-btn
                :to="appPath('spaces')"
                color="primary"
                variant="flat"
                rounded="pill"
                class="mt-4"
                append-icon="$arrowRight"
              >
                Try Song7 free
              </v-btn>
            </v-card>
          </v-col>

          <v-col cols="12" md="5">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <div class="d-flex align-center mb-4">
                <v-icon icon="$musicNote" color="grey" size="32" class="mr-3" />
                <strong class="text-h6">OnSong</strong>
              </div>
              <p class="text-body-2 mb-3">
                Apple-only chord chart and performance app for individual musicians. Deep hardware support (foot pedals,
                MIDI, backing tracks, autoscroll) with optional team sharing.
              </p>
              <ul class="compare-quick-facts">
                <li><strong>Platforms:</strong> iPad, iPhone, Mac (no Windows, no Android)</li>
                <li><strong>Built for:</strong> Individual musicians first</li>
                <li><strong>Pricing:</strong> Free version, then $29.99–$59.99/year per person</li>
              </ul>
              <v-btn
                href="https://onsongapp.com"
                target="_blank"
                rel="noopener nofollow"
                variant="outlined"
                rounded="pill"
                class="mt-4"
                append-icon="$arrowRight"
              >
                View OnSong
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Quick verdict -->
    <section class="bg-surface-bright">
      <v-container class="py-12 py-md-16">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Quick verdict</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">
          The short answer for the people who just want to know.
        </p>
        <v-row>
          <v-col cols="12" md="6">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <h3 class="text-h6 font-weight-bold mb-3">Choose OnSong if&hellip;</h3>
              <ul class="verdict-list">
                <li>
                  You and your team are entirely on Apple devices (iPad, iPhone, and Mac) and have no Windows or Android
                  in the mix.
                </li>
                <li>
                  You want deep performance hardware: foot-pedal page turns, MIDI automation, DMX stage lighting, and
                  programmable autoscroll with a timeline.
                </li>
                <li>
                  You work primarily as an individual musician and want a feature-rich personal chart reader at a low
                  per-person price.
                </li>
              </ul>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <CompareHighlightCard>
              <h3 class="text-h6 font-weight-bold mb-3">Choose Song7 if&hellip;</h3>
              <ul class="verdict-list">
                <li>
                  Your team is mixed: laptops, phones, tablets, Mac, Windows, Android. OnSong can't run on Windows or
                  Android; Song7 runs in any browser.
                </li>
                <li>
                  You want everyone in sync: one shared library, setlists and key changes that push to the whole team in
                  real time, not a per-device personal app.
                </li>
                <li>
                  You want one team subscription instead of buying a seat (with device limits) for every musician.
                </li>
              </ul>
            </CompareHighlightCard>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- At-a-glance comparison table -->
    <section>
      <v-container class="py-12 py-md-16">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">At-a-glance comparison</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center mx-auto" style="max-width: 720px">
          Feature-by-feature snapshot of how Song7 and OnSong line up.
        </p>

        <v-card class="overflow-hidden" elevation="0" border rounded="xl">
          <v-table density="comfortable" class="compare-table">
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col" class="text-center">Song7</th>
                <th scope="col" class="text-center">OnSong</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="row in matrix" :key="row.feature">
                <tr v-if="row.group" class="compare-table-group">
                  <th scope="rowgroup" colspan="3" class="text-uppercase text-caption font-weight-bold">
                    {{ row.group }}
                  </th>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ row.feature }}</td>
                  <td class="text-center">
                    <v-icon v-if="row.song7 === true" icon="$check" color="success" aria-label="Yes" />
                    <v-icon v-else-if="row.song7 === false" icon="$close" color="grey" aria-label="No" />
                    <span v-else class="text-caption">{{ row.song7 }}</span>
                  </td>
                  <td class="text-center">
                    <v-icon v-if="row.onSong === true" icon="$check" color="success" aria-label="Yes" />
                    <v-icon v-else-if="row.onSong === false" icon="$close" color="grey" aria-label="No" />
                    <span v-else class="text-caption">{{ row.onSong }}</span>
                  </td>
                </tr>
              </template>
            </tbody>
          </v-table>
        </v-card>
        <p class="text-caption text-medium-emphasis mt-3 text-center">
          Sources: onsongapp.com, onsongapp.com/pricing, OnSong support docs. Verified June 2026.
        </p>

        <div class="text-center mt-8">
          <v-btn
            :to="appPath('spaces')"
            color="primary"
            variant="flat"
            size="large"
            rounded="pill"
            append-icon="$arrowRight"
          >
            Start your free Song7 space
          </v-btn>
          <p class="text-caption text-medium-emphasis mt-2">No credit card required</p>
        </div>
      </v-container>
    </section>

    <!-- What is OnSong -->
    <section class="bg-surface-bright">
      <v-container class="py-12 py-md-16">
        <v-row>
          <v-col cols="12" md="6">
            <h2 class="text-h4 font-weight-bold mb-4">What is OnSong?</h2>
            <p class="text-body-1 mb-4">
              OnSong is a chord chart and performance app for musicians, often described on its own site as "the Swiss
              Army Knife of apps for musicians." It stores your songs, displays chord charts, and gives a performer deep
              control on stage: transposition, autoscroll, foot-pedal page turns, backing tracks, and MIDI/DMX stage
              automation.
            </p>
            <p class="text-body-1 mb-4">
              OnSong is an Apple-only product. Per OnSong's support documentation it runs on iPad, iPhone, and iPod
              Touch (iOS/iPadOS 15+) and on Intel or Apple Silicon Macs. A previous Android build was removed from the
              Play Store in 2022, and there is no Windows version. If anyone on your team isn't on Apple hardware,
              OnSong isn't an option for them.
            </p>
            <p class="text-body-1 mb-0">
              Its center of gravity is the individual musician. Team sharing exists (through OnSong Connect and Group
              plans with shared OnSong Drive storage), but it's layered on top of a personal app, and standard plans cap
              the number of devices per account.
            </p>
          </v-col>
          <v-col cols="12" md="6">
            <h2 class="text-h4 font-weight-bold mb-4">What is Song7?</h2>
            <p class="text-body-1 mb-4">
              Song7 is a standalone worship music platform built for the whole team's workflow: managing your song
              library, building setlists for Sunday, transposing on the fly, syncing the team in real time, and running
              the presentation. One tool, one team subscription.
            </p>
            <p class="text-body-1 mb-4">
              It runs in any modern browser and installs as a Progressive Web App on iOS, Android, macOS, ChromeOS, and
              Windows. No app-store gatekeeping and no platform exclusions. Every team member gets access directly in
              their browser, on whatever device they already own.
            </p>
            <p class="text-body-1 mb-0">
              Song7 is a private worship-planning tool. The songs, lyrics, and chord charts you add live only inside
              your own team's Space. Song7 does not ship a public catalogue of copyrighted worship music. You are
              responsible for holding the licenses you need for the content you store and share.
            </p>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Feature deep dive -->
    <section>
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Feature-by-feature deep dive</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">
          Where each product wins, where each one falls short.
        </p>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Platform support</h3>
          <p class="mb-3">
            <strong>OnSong:</strong> Apple-only. iPad, iPhone, iPod Touch, and Mac. No Windows version, and the Android
            app was withdrawn from the Play Store in 2022. For an all-Apple team this is a non-issue; for a mixed team
            it's a hard wall.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Any modern browser, desktop or mobile, and installable as a Progressive Web App on
            iOS, Android, macOS, ChromeOS, and Windows. The volunteer who shows up with an Android phone or a Windows
            laptop is covered without a second thought.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Individual app or team platform</h3>
          <p class="mb-3">
            <strong>OnSong:</strong> Designed around one musician and their library. It's excellent at that. Sharing
            across a team is possible through OnSong Connect and Group plans, but it's an add-on to a fundamentally
            personal app, and individual plans limit you to 2–4 devices.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Built team-first from the start. One shared song library, setlists the whole team
            sees, and changes that sync in real time. There's no per-device seat math. Members open it in a browser and
            they're in.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Song and chord chart management</h3>
          <p class="mb-3">
            <strong>OnSong:</strong> Mature chart handling: ChordPro, PDF chord charts, a document scanner for printed
            sheets, Nashville number-system display, chord diagrams, and integrations with licensed chart sources like
            MultiTracks and SongSelect. Playing from a PDF as your stage chart is a real OnSong strength Song7 doesn't
            match.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Built-in editing of your own charts. Import from ChordPro or plain text with chord
            brackets, or snap a photo of a printed sheet and Song7's photo-to-chart OCR turns it into an editable chart
            (not just a stored PDF). For sheet music, a song can hold MusicXML scores (one per instrument if you like)
            rendered as staff notation you can transpose, and PDFs or images can be attached and viewed alongside the
            chart, though they stay stored files rather than editable charts. The library is yours and your team's.
            Song7 doesn't provide a public database of copyrighted songs.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Setlist creation and planning</h3>
          <p class="mb-3">
            <strong>OnSong:</strong> Supports sets you build on your device. Sharing a set with bandmates runs through
            OnSong Connect; the planning model is still rooted in the individual's app rather than a shared team plan.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Drag-and-drop setlist builder is core. Pick songs, reorder by dragging, set the key
            per song, publish the set, and the whole team sees it instantly, with no per-device syncing step to think
            about.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Transposition and key changes</h3>
          <p class="mb-3">
            <strong>OnSong:</strong> Strong, fast transposition and capo formatting on a per-device basis. Change the
            key on your iPad and your chart updates, but it's your copy.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> One-tap transposition into any key, and because the library is shared, changing a
            song's key for Sunday updates every musician's chart in real time. No "did everyone re-sync?" moment.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Live performance hardware</h3>
          <p class="mb-3">
            <strong>OnSong:</strong> This is OnSong's home turf. Foot-pedal page turns (including AirTurn), programmable
            autoscroll with a timeline, backing tracks linked to Apple Music or Spotify, audio click and visual
            metronome, MIDI automation, and DMX stage lighting. For a performance-heavy setup, OnSong goes deeper than
            Song7.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Covers the essentials: autoscroll, a built-in metronome, a built-in tuner, and
            backing tracks or stems your team uploads to a song, played in sync with a per-track mix. It doesn't ship
            curated foot-pedal hardware or MIDI/DMX automation. If hands-free pedals and stage automation are central to
            your service, OnSong wins this category today.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Annotations and notes</h3>
          <p class="mb-3">
            <strong>OnSong:</strong> Rich annotation tools on Premium: drawings, clips, and sticky notes layered onto
            charts. One of its stronger areas for musicians who mark up heavily.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Each song has a notes section the whole team sees. Heavy free-form drawing on top of
            a chart isn't the focus. Song7 favors structured editing of the chart itself over overlay markup.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Live presentation and projection</h3>
          <p class="mb-3">
            <strong>OnSong:</strong> Premium includes lyrics projection, stage monitors, and Google Cast output to an
            external display, driven from an Apple device.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Has a congregation-facing presentation mode that displays clean, full-screen lyrics
            on any screen with a browser, and no Apple hardware is required to drive it. Both products can project;
            Song7 does it without tying you to the Apple ecosystem.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Team collaboration and sync</h3>
          <p class="mb-3">
            <strong>OnSong:</strong> Collaboration happens through OnSong Connect and Group plans with shared OnSong
            Drive storage. It works, but it's a sharing layer on top of individual libraries rather than a single shared
            source of truth.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Real-time team sync is the core model. Multiple editors manage one shared library
            and setlists, members see published sets instantly, and multi-Space support lets multi-campus churches keep
            separate teams under one account.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Offline access</h3>
          <p class="mb-3">
            <strong>OnSong:</strong> As a native Apple app, charts are stored on-device and read offline without a
            thought. This is a genuine strength of a mature native app when the church Wi-Fi drops mid-service.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> As a PWA, Song7 caches content for offline reading, but the offline experience is
            currently lighter than a fully native app. For situations where rock-solid offline is non-negotiable,
            OnSong's native caching has the edge.
          </p>
        </article>
      </v-container>
    </section>

    <!-- Product showcase: where Song7 wins -->
    <section>
      <v-container class="py-12 py-md-16">
        <div class="text-center mb-10">
          <p class="text-primary font-weight-bold mb-2">Beyond the comparison</p>
          <h2 class="text-h4 font-weight-bold mb-3">Things Song7 does that OnSong can't</h2>
          <p class="text-h6 text-medium-emphasis mx-auto" style="max-width: 720px">
            The six things teams moving from OnSong mention most.
          </p>
        </div>

        <v-row>
          <v-col v-for="card in showcase" :key="card.title" cols="12" sm="6" lg="4">
            <v-card class="pa-6 h-100 showcase-card" elevation="0" border rounded="xl">
              <div class="showcase-icon mb-4">
                <v-icon :icon="card.icon" size="24" />
              </div>
              <h3 class="text-h6 font-weight-bold mb-2">{{ card.title }}</h3>
              <p class="text-body-2 mb-0">{{ card.text }}</p>
            </v-card>
          </v-col>
        </v-row>

        <div class="text-center mt-10">
          <v-btn to="/" color="primary" variant="text" rounded="pill" append-icon="$arrowRight">
            See the full Song7 feature tour
          </v-btn>
        </div>
      </v-container>
    </section>

    <!-- Pricing comparison -->
    <section class="bg-surface-bright">
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Pricing comparison</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">What you actually pay, and what you get for it.</p>

        <v-row>
          <v-col cols="12" md="6">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <h3 class="text-h6 font-weight-bold mb-3">OnSong cost</h3>
              <p class="text-body-2 mb-3">
                OnSong is priced per person. The plans below are what one musician pays; a full band means a seat each.
              </p>
              <ul class="price-breakdown">
                <li><strong>Free version:</strong> $0, basic library and song viewing</li>
                <li>
                  <strong>Essentials:</strong> $29.99/year (or $3.99/mo): unlimited songs, sets, autoscroll, foot
                  pedals, transposition, cloud integrations, 2&nbsp;GB OnSong Drive, up to 2 devices
                </li>
                <li>
                  <strong>Premium:</strong> $59.99/year (or $5.99/mo): everything in Essentials plus annotations,
                  backing tracks, document scanner, lyrics projection, MIDI/stage lighting, browser access, 5&nbsp;GB
                  OnSong Drive, up to 4 devices
                </li>
                <li><strong>Group plans:</strong> per-user pricing with shared OnSong Drive storage</li>
              </ul>
              <p class="text-caption mt-3 mb-0">
                Source: onsongapp.com/pricing, verified June 2026. Numbers may change.
              </p>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <CompareHighlightCard>
              <h3 class="text-h6 font-weight-bold mb-3">Song7 cost</h3>
              <p class="text-body-2 mb-3">
                Song7 is priced as one plan for your team, not per individual musician, and without per-device limits to
                manage.
              </p>
              <p class="text-body-2 mb-4">
                Song7 is free for teams of up to {{ FREE_PLAN.members }} members, with every feature included and no
                credit card required. Paid plans for bigger teams come with a 30-day trial.
              </p>
              <v-btn to="/pricing" color="primary" variant="flat" rounded="pill" append-icon="$arrowRight">
                See Song7 pricing
              </v-btn>
              <p class="text-caption mt-4 mb-0">One subscription for the whole team. No per-seat math.</p>
            </CompareHighlightCard>
          </v-col>
        </v-row>

        <v-card class="mt-8 pa-6" elevation="0" color="warning" variant="tonal" rounded="xl">
          <h3 class="text-h6 font-weight-bold mb-2">Where the pricing models actually differ</h3>
          <p class="text-body-2 mb-0">
            For a single user, Song7 is actually free. Its free plan costs nothing, while OnSong has a free version and
            charges $59.99/year for Premium, which adds its deeper performance tools. The models really diverge on
            teams: OnSong is priced per person with a device cap, so a six-piece band is six seats; Song7 is free for up
            to {{ FREE_PLAN.members }} members and moves to one flat team plan only beyond that, with no per-seat math.
            If you're equipping a mixed-device team, compare OnSong's per-seat total against Song7's single team plan.
          </p>
        </v-card>

        <v-alert type="info" variant="tonal" class="mt-4" density="comfortable" icon="$informationOutline">
          <strong>Honest note on the comparison:</strong> If you're a solo worship leader or a small all-Apple band that
          mostly needs a personal chart reader with deep performance tools (pedals, MIDI, backing tracks), OnSong is
          excellent, and its paid app is inexpensive. Song7's free plan covers the basics, not that performance depth.
          The Song7 case is strongest when you're coordinating a team across mixed devices and want one shared,
          always-in-sync source of truth instead of per-person apps.
        </v-alert>

        <div class="text-center mt-8">
          <v-btn to="/pricing" color="primary" variant="outlined" size="large" rounded="pill" append-icon="$arrowRight">
            See Song7 pricing in full
          </v-btn>
        </div>
      </v-container>
    </section>

    <!-- Migration -->
    <section id="migrate">
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-4">Migrating from OnSong to Song7</h2>
        <p class="text-body-1 mb-4">
          Moving from OnSong to Song7 means taking your charts out of a personal Apple app and into a shared team
          library. The practical steps:
        </p>
        <ol class="migration-steps">
          <li>
            <strong>Export your songs from OnSong.</strong> OnSong can export and share your library; save your charts
            in a portable format Song7 accepts: ChordPro or plain text with chord brackets. ChordPro preserves chord
            positions and parses cleanly. For charts you only hold as a PDF or printed sheet, Song7's photo-to-chart
            capture is the fallback path, or attach the PDF to the song as-is, to view alongside the chart.
          </li>
          <li>
            <strong>Create a Song7 account and a Space for your church.</strong> Free for teams of up to
            {{ FREE_PLAN.members }} members, no credit card required.
          </li>
          <li>
            <strong>Import your songs into Song7.</strong> Paste in ChordPro or text with chord brackets; Song7 parses
            the chord positions and renders the chart.
          </li>
          <li>
            <strong>Invite your team.</strong> Members get instant access in their browser on any device, including the
            Windows and Android users OnSong couldn't serve.
          </li>
          <li>
            <strong>Recreate one upcoming setlist to test the workflow</strong> before fully switching. Run a midweek
            rehearsal on Song7 to surface anything specific to your team's habits.
          </li>
        </ol>
        <v-alert type="info" variant="tonal" class="mt-6" density="comfortable" icon="$informationOutline">
          <strong>On licensing:</strong> Song7 stores the worship materials you add privately within your team's Space.
          You're responsible for holding the CCLI, SongSelect, publisher, or other permissions required for the content
          you store, copy, or share. Only import or export songs you're licensed or permitted to use.
        </v-alert>
        <p class="text-body-1 mt-4 mb-0">
          If you're a solo musician who just wants a feature-rich personal reader with foot pedals and MIDI on your
          iPad, OnSong is doing the right job and Song7 isn't the swap for you. Song7 earns its place when a team needs
          to share one library across devices.
        </p>
      </v-container>
    </section>

    <!-- Decision framework -->
    <section class="bg-surface-bright">
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Which one should you pick?</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">A short decision guide based on your situation.</p>

        <v-row>
          <v-col cols="12" md="6">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <h3 class="text-h6 font-weight-bold mb-3">Stay with (or pick) OnSong if</h3>
              <ul class="verdict-list">
                <li>Everyone on your team is on Apple hardware.</li>
                <li>Foot-pedal page turns, MIDI automation, or DMX stage lighting are part of how you play.</li>
                <li>You rely on programmable autoscroll with a timeline, or PDF chord charts.</li>
                <li>Heavy chart annotation is core to how you rehearse.</li>
                <li>You're an individual musician who wants a deep personal reader at a low price.</li>
              </ul>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <CompareHighlightCard>
              <h3 class="text-h6 font-weight-bold mb-3">Switch to (or pick) Song7 if</h3>
              <ul class="verdict-list">
                <li>Your team mixes Windows, Android, Mac, iPad, and phones.</li>
                <li>You want one shared library and setlists that sync to everyone in real time.</li>
                <li>You'd rather buy one team plan than a seat-with-device-limit per musician.</li>
                <li>You want to onboard volunteers in a browser without app-store friction.</li>
                <li>Drag-and-drop setlist building and shared transposition matter more than stage automation.</li>
              </ul>
            </CompareHighlightCard>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Social proof -->
    <section class="bg-surface-bright">
      <ClientOnly>
        <FeaturedFeedback class="py-8 py-md-12" />
      </ClientOnly>
    </section>

    <!-- FAQ -->
    <section>
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Frequently asked questions</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">
          The questions worship leaders actually ask before switching.
        </p>

        <FaqAccordion :groups="[{ items: faqItems }]" />
      </v-container>
    </section>

    <!-- Was this comparison helpful -->
    <section>
      <v-container class="py-4" style="max-width: 720px">
        <PageFeedback title="OnSong" context="Comparison" question="Was this comparison helpful?" />
      </v-container>
    </section>

    <!-- Final CTA -->
    <section>
      <v-container class="py-12 py-md-16 text-center" style="max-width: 720px">
        <h2 class="text-h4 font-weight-bold mb-4">Ready to try Song7?</h2>
        <p class="text-h6 text-medium-emphasis mb-6">
          Free for teams of up to {{ FREE_PLAN.members }} members, no credit card required. Bring your team across
          whenever you're ready.
        </p>
        <v-btn
          :to="appPath('spaces')"
          color="primary"
          variant="flat"
          size="x-large"
          rounded="pill"
          append-icon="$arrowRight"
          class="pulse-button"
        >
          Start free
        </v-btn>
        <p class="text-caption text-medium-emphasis mt-4">
          Questions? <NuxtLink to="/contact">Talk to us first</NuxtLink>.
        </p>
      </v-container>
    </section>

    <!-- Related comparisons -->
    <CompareRelated current-slug="onsong" />
  </div>
</template>

<script setup>
import { useSeo } from '~/composables/useSeo'
import { FREE_PLAN } from '@@/constants/publicPlans'

definePageMeta({
  layout: 'website',
})

const showcase = [
  {
    icon: '$earth',
    title: 'Runs on Windows and Android too',
    text: 'OnSong is Apple-only (iPad, iPhone, and Mac). Song7 runs in any browser, so the volunteer with a Windows laptop or Android phone is covered without a second device.',
  },
  {
    icon: '$sync',
    title: 'Real-time team sync',
    text: 'Change a key or publish a setlist and every musician sees it instantly. OnSong shares across devices, but it layers sync on top of personal libraries rather than one shared source of truth.',
  },
  {
    icon: '$camera',
    title: 'Photo-to-chart OCR import',
    text: "Snap a picture of a printed chord sheet and Song7 turns it into an editable chart. OnSong's document scanner stores a PDF; Song7 gives you a chart you can actually edit and transpose.",
  },
  {
    icon: '$formatListBulleted',
    title: 'Shared drag-and-drop setlists',
    text: "Build, reorder, and publish Sunday's setlist and the whole team sees it live, with no per-device sharing step to remember.",
  },
  {
    icon: '$domain',
    title: 'Multi-Space for multi-campus churches',
    text: 'Run separate song libraries, setlists, and teams per campus or worship team, all under one account.',
  },
  {
    icon: '$accountGroup',
    title: 'One team plan, no per-device caps',
    text: 'Everything is part of a single team subscription. OnSong is priced per person and caps standard plans at 2–4 devices each.',
  },
]

const matrix = [
  // Product structure
  {
    group: 'Product structure',
    feature: 'Standalone product (no other subscription required)',
    song7: true,
    onSong: true,
  },
  { feature: 'Team-first model (shared library & sync at the core)', song7: true, onSong: false },
  { feature: 'Free tier available', song7: `Up to ${FREE_PLAN.members} members`, onSong: 'Free version' },
  { feature: 'One subscription covers the whole team', song7: true, onSong: false },
  { feature: 'No per-device limit on standard plans', song7: true, onSong: '2–4 devices' },

  // Platforms
  { group: 'Platforms', feature: 'Runs in any browser', song7: true, onSong: false },
  { feature: 'Windows desktop support', song7: true, onSong: false },
  { feature: 'Android phone & tablet', song7: true, onSong: false },
  { feature: 'macOS support', song7: 'Browser / PWA', onSong: 'Native Mac app' },
  { feature: 'iPad', song7: true, onSong: true },
  { feature: 'iOS (iPhone)', song7: true, onSong: true },
  { feature: 'Native Apple app (App Store)', song7: false, onSong: true },
  { feature: 'Installable as PWA (home-screen)', song7: true, onSong: false },
  { feature: 'Usable without any Apple device', song7: true, onSong: false },

  // Song & chart management
  { group: 'Song & chart management', feature: 'Your own private song library', song7: true, onSong: true },
  { feature: 'Edit chord charts in-app', song7: true, onSong: true },
  { feature: 'ChordPro import', song7: true, onSong: true },
  { feature: 'Plain-text lyrics + chord brackets import', song7: true, onSong: true },
  { feature: 'PDF chord charts as the stage chart', song7: 'Attach & view', onSong: true },
  { feature: 'MusicXML notation (rendered, transposable)', song7: true, onSong: 'Not documented' },
  { feature: 'Document scanner for printed sheets', song7: 'Photo-to-chart OCR', onSong: true },
  { feature: 'Photo OCR to an editable chart', song7: true, onSong: false },
  { feature: 'Built-in chord diagrams', song7: true, onSong: true },
  { feature: 'Capo support', song7: true, onSong: true },
  { feature: 'Nashville number-system display', song7: false, onSong: true },
  {
    feature: 'Integrations with licensed chart sources (MultiTracks, SongSelect)',
    song7: false,
    onSong: true,
  },

  // Setlists & planning
  { group: 'Setlists & planning', feature: 'Drag-and-drop setlist builder', song7: true, onSong: true },
  { feature: 'Setlists shared with the whole team in real time', song7: true, onSong: 'Via OnSong Connect' },
  { feature: 'Per-song key in setlist', song7: true, onSong: true },
  { feature: 'Set history (see what was played before)', song7: true, onSong: 'Not documented' },

  // Transposition
  { group: 'Transposition', feature: 'One-tap transposition into any key', song7: true, onSong: true },
  { feature: 'Key change pushes to the whole team in real time', song7: true, onSong: false },

  // Live use
  { group: 'Live use & performance', feature: 'Congregation-facing lyrics projection', song7: true, onSong: true },
  { feature: 'Autoscroll', song7: true, onSong: true },
  { feature: 'Programmable autoscroll / timeline', song7: false, onSong: true },
  { feature: 'Built-in metronome', song7: true, onSong: true },
  { feature: 'Built-in tuner', song7: true, onSong: 'Not documented' },
  { feature: 'Backing tracks & stems (uploaded, played in sync)', song7: true, onSong: true },
  { feature: 'Reference recordings (Spotify, Apple Music, YouTube)', song7: true, onSong: true },
  { feature: 'Foot-pedal page turn (AirTurn hardware)', song7: false, onSong: true },
  { feature: 'MIDI automation & DMX stage lighting', song7: false, onSong: true },
  { feature: 'Free-form annotations (drawings, clips, sticky notes)', song7: 'Notes only', onSong: true },
  { feature: 'Native offline mode', song7: 'PWA cache', onSong: true },

  // Team
  {
    group: 'Team & collaboration',
    feature: 'Real-time team sync of plan & chart changes',
    song7: true,
    onSong: false,
  },
  { feature: 'Multi-team / multi-campus separation', song7: 'Multi-Space', onSong: 'Multiple libraries' },
  { feature: 'Browser-based access (no app install)', song7: true, onSong: 'OnSong Console (Premium)' },
  { feature: 'Shared team storage', song7: true, onSong: 'Group plan (OnSong Drive)' },
  { feature: 'Notes on songs', song7: 'Shared with the team', onSong: true },
]

const faqItems = [
  {
    question: 'Song7 vs OnSong: what’s the real difference?',
    answer:
      'The core difference is who each tool is built for. OnSong is an Apple-only app built around the individual performer, with deep on-device performance hardware like foot-pedal page turns, MIDI, DMX, and backing tracks. Song7 is a cross-platform, team-first web app built around a shared library, real-time setlist and key sync, and live presentation for a whole worship team, including members on Windows and Android. Pick OnSong if solo performance hardware on Apple devices is central; pick Song7 if coordinating a team across mixed devices is.',
  },
  {
    question: 'Is OnSong available on Android or Windows?',
    answer:
      "No. OnSong is an Apple-only app. Per OnSong's support documentation it runs on iPad, iPhone, and iPod Touch (iOS/iPadOS 15+) and on Intel or Apple Silicon Macs. A previous Android build was removed from the Google Play Store in 2022, and there is no Windows version. If anyone on your team uses an Android phone or a Windows laptop, OnSong is not an option for them, whereas Song7 runs in any browser.",
  },
  {
    question: 'Is OnSong free?',
    answer:
      'OnSong offers a free version with basic functionality to build a library and view songs. Full features require a subscription: Essentials at $29.99/year (or $3.99/month) and Premium at $59.99/year (or $5.99/month), priced per person. Group plans are available with per-user pricing and shared OnSong Drive storage. Verify current numbers on onsongapp.com/pricing.',
  },
  {
    question: "What's the best alternative to OnSong?",
    answer:
      "It depends on what you need OnSong for. If you want a cross-platform, team-first tool that puts one shared song library, setlists, and live sync in front of a whole worship team, including members on Windows and Android, Song7 is the strongest alternative. If you specifically need OnSong's deep performance hardware (foot pedals, MIDI, DMX, backing tracks) on Apple devices, there isn't a like-for-like replacement, and OnSong may still be the right tool.",
  },
  {
    question: 'Can Song7 do everything OnSong does?',
    answer:
      "No, and that's the honest answer. OnSong goes deeper on individual-performer hardware: foot-pedal page turns, programmable autoscroll, MIDI automation, DMX stage lighting, PDF chord charts, and heavy chart annotation. Song7 covers the shared-team workflow OnSong handles less directly: one shared library, real-time setlist and key sync, drag-and-drop planning, multi-campus Spaces, and cross-platform access. If performance hardware is central, OnSong wins; if team coordination across mixed devices is central, Song7 wins.",
  },
  {
    question: 'Does Song7 work on iPad?',
    answer:
      'Yes. Song7 runs in any modern browser on iPad and installs as a Progressive Web App from Safari for a fullscreen, home-screen-launchable experience. There is no separate native iPad app required.',
  },
  {
    question: 'Does Song7 work on Windows and Android?',
    answer:
      'Yes. Song7 runs in any browser on Windows, Android, macOS, ChromeOS, and iOS. This is a key difference from OnSong, which is Apple-only. For a worship team where not everyone owns an iPad or iPhone, Song7 covers every member without anyone needing to buy Apple hardware.',
  },
  {
    question: 'Can I import my songs from OnSong into Song7?',
    answer:
      "Yes. OnSong can export your library, and Song7 accepts ChordPro files and plain text with chord brackets, both formats OnSong can produce. ChordPro preserves chord positions and parses cleanly. For charts you only hold as a PDF or printed sheet, Song7's photo-to-chart capture works as a fallback, and the PDF itself can be attached to the song and viewed as-is. You're responsible for having the licenses needed for any songs you transfer.",
  },
  {
    question: 'Does Song7 support foot-pedal page turns and backing tracks?',
    answer:
      'Backing tracks, yes; foot pedals, not as a first-class feature. A Song7 song can hold its own audio (backing track, click, guide vocal, or a stem per instrument), played in sync with mute, solo, and a fader per track. What Song7 does not ship is a curated foot-pedal experience or MIDI/DMX automation the way OnSong does, so if hands-free hardware pedals and stage automation are critical to your service, OnSong has the edge today.',
  },
  {
    question: 'Is Song7 cheaper than OnSong?',
    answer: `For a single user, Song7 is effectively free. Its free plan costs nothing, while OnSong's full app is $59.99/year for Premium (OnSong also has a limited free version). OnSong's paid tier adds deeper performance tools, so 'cheaper' and 'more capable for a solo performer' can point in different directions. For a team, OnSong is priced per person with a device cap, while Song7 is free for up to ${FREE_PLAN.members} members and moves to one flat team plan beyond that, so for most multi-person teams Song7 works out cheaper. See song7.io/pricing for current numbers.`,
  },
  {
    question: 'Does Song7 give me a database of worship songs to use?',
    answer:
      "No. Song7 is a private worship-planning tool, not a public catalogue of copyrighted music. The songs, lyrics, and chord charts in Song7 are the ones you add, stored privately inside your own team's Space. You are responsible for holding the CCLI, SongSelect, publisher, or other licenses required for the content you store, copy, or share.",
  },
  {
    question: 'Does OnSong support team sharing?',
    answer:
      "Yes, through OnSong Connect and Group plans with shared OnSong Drive storage. The difference is structural: OnSong builds sharing on top of an individual musician's app, while Song7 is team-first, with one shared library and real-time sync as the default model rather than an add-on.",
  },
  {
    question: 'Can I use Song7 without internet during a service?',
    answer:
      "Song7 caches content for offline use as a Progressive Web App, which covers most short connectivity drops. OnSong, as a native Apple app, stores charts on-device and is more battle-tested offline. For services where rock-solid offline mode is non-negotiable, OnSong's native caching currently has the edge.",
  },
]

useSeo({
  keywords: [
    'onsong alternative',
    'best onsong alternative',
    'onsong for android',
    'onsong for windows',
    'onsong alternative for worship teams',
    'song7 vs onsong',
    'onsong vs song7',
    'onsong review',
    'cross-platform chord chart app',
    'worship chord chart app',
  ],
  schema: 'SoftwareApplication',
  faq: faqItems,
})
</script>

<style scoped>
.compare-hero {
  background:
    radial-gradient(1000px 600px at 80% 0%, rgba(var(--v-theme-primary), 0.12), transparent 60%),
    radial-gradient(800px 500px at 0% 30%, rgba(var(--v-theme-secondary), 0.08), transparent 60%);
}

.compare-h1 {
  font-size: clamp(34px, 5vw, 56px);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.1;
}

.compare-quick-facts {
  list-style: none;
  padding: 0;
  margin: 0;
}

.compare-quick-facts li {
  padding: 4px 0;
  font-size: 0.95rem;
}

.verdict-list,
.migration-steps {
  padding-left: 1.25rem;
  margin: 0;
}

.verdict-list li,
.migration-steps li {
  margin-bottom: 0.6rem;
  line-height: 1.55;
}

.compare-table th {
  font-weight: 700 !important;
  font-size: 0.95rem;
}

.deep-dive {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  padding: 1.75rem 0;
}

.deep-dive:last-child {
  border-bottom: none;
}

.deep-dive p {
  line-height: 1.65;
}

.price-breakdown {
  list-style: none;
  padding: 0;
  margin: 0;
}

.price-breakdown li {
  padding: 8px 0;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  font-size: 0.95rem;
}

.price-breakdown li:last-child {
  border-bottom: none;
}

.pulse-button {
  animation: pulseButton 2s infinite;
}

.switcher-callout {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: rgba(var(--v-theme-success), 0.1);
  color: rgb(var(--v-theme-success));
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease;
}

.switcher-callout:hover {
  background: rgba(var(--v-theme-success), 0.18);
}

.compare-table-group th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  color: rgba(var(--v-theme-on-surface), 0.6);
  letter-spacing: 0.06em;
  padding-top: 16px !important;
  padding-bottom: 8px !important;
}

.showcase-card {
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.showcase-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
}

.showcase-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
}

@keyframes pulseButton {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(var(--v-theme-primary), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0);
  }
}
</style>
